/* eslint-disable ember/no-runloop */
import { isDestroyed, isDestroying } from '@ember/destroyable';
import { action, set } from '@ember/object';
import { later } from '@ember/runloop';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { timeout } from 'lumen/utils/waiters';

interface GeneralDropdownsOtaDropdownSignature {
  Args: {
    dropdownClass?: string;
  };
}

export default class GeneralDropdownsOtaDropdown extends Component<GeneralDropdownsOtaDropdownSignature> {
  isOpen = false;

  /**
   * For some dropdown the action was triggered twice due to reasons beyond our control
   * There is a small timeout added where new events are dropped
   * This was enough to prevent the dropdown to be closed and opened with one click
   */
  toggleDropdownTask = task({ drop: true }, async () => {
    set(this, 'isOpen', !this.isOpen);

    if (this.isOpen) {
      // http://discuss.emberjs.com/t/solved-how-to-remove-event-handler-properly-in-a-component/8931/2
      // @ts-expect-error: Incorrect type
      this.privateOutsideClickHandler = this.outsideClickHandler.bind(this);

      later(() => {
        // if we attach the outsideClickHandler directly,
        // it will cause the dropdown to autoclose instantly
        if (this.isOpen) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          document.addEventListener('click', this.privateOutsideClickHandler);
        }
      }, 100);
    } else {
      this.removeOutsideClickHandler();
    }

    await timeout();
  });

  // Note: This must be a unique class across the ENTIRE app
  // (will be used to detect outside clicks that close the dropdown)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dropdownClass() {
    return this.args.dropdownClass ?? '.dropdown';
  }

  willDestroy(): void {
    super.willDestroy();

    this.removeOutsideClickHandler();
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  outsideClickHandler(e) {
    if (this.isOpen) {
      // Alternative found online:
      // https://github.com/zeppelin/ember-click-outside/blob/master/addon/mixin.js

      // Element.closest() behavior:
      // Starting with the Element itself, the closest() method traverses parents(heading toward the document root) of the Element
      // until it finds a node that matches the provided selectorString.
      // Will return itself or the matching ancestor.If no such element exists, it returns null.

      // The closest element should be the boundary of the region where we want to detect the inside click.
      // If the .closest() returns null, this means that the click target isn't a child element of the inside click region.
      let isInside = false;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      if (e.target.closest(this.dropdownClass) !== null) {
        isInside = true;
      }

      if (!isInside) {
        this.toggleDropdown();
      }
    }
  }

  removeOutsideClickHandler(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    document.removeEventListener('click', this.privateOutsideClickHandler);
  }

  @action
  toggleDropdown(): void {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.toggleDropdownTask.perform();
  }
}
